/**
 * AppRoutes — centralised route table
 * ====================================
 * Extracted from App.js so the root component stays focused on providers,
 * session bootstrap and global modals. Every lazy page import lives here
 * alongside its `<Route>` entry, keeping the full route surface viewable
 * in a single file.
 *
 * The only runtime input is the `user` object (needed by the landing-page
 * redirect on `/`).
 */

import React, { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import ProtectedRoute from '../components/ProtectedRoute';
import Landing from '../pages/Landing';
import TermsPage from '../pages/TermsPage';
import CancellationPolicy from '../pages/CancellationPolicy';
import SignUpBonusTerms from '../pages/SignUpBonusTerms';

// Redirect helper: legacy /jobs/:jobId/bids → /jobs/:jobId.
// The ".../bids" page allowed clients to bypass the interview/escrow flow,
// so we forward all incoming traffic (old emails, bookmarks) to the unified
// job detail view.
const RedirectToJobDetails = () => {
  const { jobId } = useParams();
  return <Navigate to={`/jobs/${jobId}`} replace />;
};

// ========== Lazy-loaded pages ==========
const Dashboard = lazy(() => import('../pages/Dashboard'));  // eslint-disable-line no-unused-vars
const UnifiedDashboard = lazy(() => import('../pages/UnifiedDashboard'));
const ClientDashboard = lazy(() => import('../pages/ClientDashboard'));  // eslint-disable-line no-unused-vars
const TaskHeroDashboard = lazy(() => import('../pages/TaskHeroDashboard'));  // eslint-disable-line no-unused-vars
const CreateJob = lazy(() => import('../pages/CreateJob'));
const BrowseJobs = lazy(() => import('../pages/BrowseJobs'));
const JobDetails = lazy(() => import('../pages/JobDetails'));
const Messages = lazy(() => import('../pages/Messages'));
const Calendar = lazy(() => import('../pages/Calendar'));
const JobTracking = lazy(() => import('../pages/JobTracking'));
const TrackingHistory = lazy(() => import('../pages/TrackingHistory'));
const PaymentSuccess = lazy(() => import('../pages/PaymentSuccess'));
const Profile = lazy(() => import('../pages/Profile'));
const HeroSettingsPage = lazy(() => import('../pages/HeroSettingsPage'));
const PerformanceStats = lazy(() => import('../pages/PerformanceStats'));
const MyEarnings = lazy(() => import('../pages/MyEarnings'));
const Transactions = lazy(() => import('../pages/Transactions'));
const MoneyTransfers = lazy(() => import('../pages/MoneyTransfers'));
const MyDisputes = lazy(() => import('../pages/MyDisputes'));
const UnifiedVerification = lazy(() => import('../pages/UnifiedVerification'));
const Reports = lazy(() => import('../pages/Reports'));
const AboutUs = lazy(() => import('../pages/AboutUs'));
const ContactUs = lazy(() => import('../pages/ContactUs'));
const PrivacyPolicy = lazy(() => import('../pages/PrivacyPolicy'));
const CookiePolicy = lazy(() => import('../pages/CookiePolicy'));
const Agreements = lazy(() => import('../pages/Agreements'));
const HelpCenter = lazy(() => import('../pages/HelpCenter'));
const Safety = lazy(() => import('../pages/Safety'));
const AccessibilityStatement = lazy(() => import('../pages/AccessibilityStatement'));
const Referrals = lazy(() => import('../pages/Referrals'));
const AdminLogin = lazy(() => import('../pages/AdminLogin'));
const AdminDashboard = lazy(() => import('../pages/AdminDashboard'));
const AdminPanel = lazy(() => import('../pages/AdminPanel'));
const Reviews = lazy(() => import('../pages/Reviews'));
const PublicProfile = lazy(() => import('../pages/PublicProfile'));
const Invoices = lazy(() => import('../pages/Invoices'));
const Wallet = lazy(() => import('../pages/Wallet'));
const FeeCalculator = lazy(() => import('../pages/FeeCalculator'));
const JobsAppliedPage = lazy(() => import('../pages/JobsAppliedPage'));
const NotFound = lazy(() => import('../pages/NotFound'));
const SoundTest = lazy(() => import('../pages/SoundTest'));

const Unauthorized = lazy(() => import('../pages/errors').then((m) => ({ default: m.Unauthorized })));
const Forbidden = lazy(() => import('../pages/errors').then((m) => ({ default: m.Forbidden })));
const ServerError = lazy(() => import('../pages/errors').then((m) => ({ default: m.ServerError })));
const ServiceUnavailable = lazy(() => import('../pages/errors').then((m) => ({ default: m.ServiceUnavailable })));


const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500" data-testid="page-loader-spinner" />
  </div>
);


export default function AppRoutes({ user }) {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={user ? <Navigate to="/dashboard" /> : <Landing />} />

        {/* Protected Routes */}
        <Route path="/dashboard" element={<ProtectedRoute showModal={true}><UnifiedDashboard /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute showModal={true}><Profile /></ProtectedRoute>} />
        <Route path="/hero-settings" element={<ProtectedRoute showModal={true}><HeroSettingsPage /></ProtectedRoute>} />
        <Route path="/performance" element={<ProtectedRoute showModal={true}><PerformanceStats /></ProtectedRoute>} />
        <Route path="/create-job" element={<ProtectedRoute showModal={true}><CreateJob /></ProtectedRoute>} />
        <Route path="/jobs/:jobId/bids" element={<RedirectToJobDetails />} />
        <Route path="/edit-job/:jobId" element={<ProtectedRoute showModal={true}><CreateJob /></ProtectedRoute>} />
        <Route path="/repost-job/:jobId" element={<ProtectedRoute showModal={true}><CreateJob /></ProtectedRoute>} />
        <Route path="/tracking/:jobId" element={<ProtectedRoute showModal={true}><JobTracking /></ProtectedRoute>} />
        <Route path="/tracking-history" element={<ProtectedRoute><TrackingHistory /></ProtectedRoute>} />
        <Route path="/become-task-hero" element={<Navigate to="/profile" replace />} />
        <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
        <Route path="/earnings-dashboard" element={<Navigate to="/my-earnings" replace />} />
        <Route path="/transactions" element={<ProtectedRoute><Transactions /></ProtectedRoute>} />
        <Route path="/money-transfers" element={<ProtectedRoute><MoneyTransfers /></ProtectedRoute>} />
        <Route path="/my-disputes" element={<ProtectedRoute><MyDisputes /></ProtectedRoute>} />
        <Route path="/verify" element={<ProtectedRoute><UnifiedVerification /></ProtectedRoute>} />
        <Route path="/verification" element={<Navigate to="/verify" replace />} />
        <Route path="/hero-verification" element={<Navigate to="/verify" replace />} />
        <Route path="/client-verification" element={<Navigate to="/verify" replace />} />
        <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute showModal={true}><Messages /></ProtectedRoute>} />
        <Route path="/calendar" element={<ProtectedRoute showModal={true}><Calendar /></ProtectedRoute>} />
        <Route path="/referrals" element={<ProtectedRoute showModal={true}><Referrals /></ProtectedRoute>} />

        {/* Reviews Page */}
        <Route path="/reviews" element={<ProtectedRoute showModal={true}><Reviews /></ProtectedRoute>} />
        <Route path="/reviews/:userId" element={<ProtectedRoute showModal={true}><Reviews /></ProtectedRoute>} />

        {/* Public Profile Page */}
        <Route path="/profile/:userId" element={<ProtectedRoute showModal={true}><PublicProfile /></ProtectedRoute>} />

        {/* Invoices Page */}
        <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />

        {/* Wallet Page */}
        <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />

        {/* Fee Calculator */}
        <Route path="/fee-calculator" element={<ProtectedRoute showModal={true}><FeeCalculator /></ProtectedRoute>} />

        {/* Hero Availability Management */}
        <Route path="/availability" element={<ProtectedRoute showModal={true}><Calendar defaultTab="availability" /></ProtectedRoute>} />

        {/* Hero Jobs Applied Page */}
        <Route path="/jobs-applied" element={<ProtectedRoute><JobsAppliedPage /></ProtectedRoute>} />

        {/* Browse Jobs */}
        <Route path="/browse-jobs" element={<ProtectedRoute showModal={true}><BrowseJobs /></ProtectedRoute>} />

        {/* Public Routes */}
        <Route path="/jobs/:jobId" element={<ProtectedRoute showModal={true}><JobDetails /></ProtectedRoute>} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/cancellation-policy" element={<CancellationPolicy />} />
        <Route path="/sign-up-bonus-terms" element={<SignUpBonusTerms />} />

        {/* Info Pages */}
        <Route path="/my-earnings" element={<ProtectedRoute showModal={true}><MyEarnings /></ProtectedRoute>} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/help" element={<HelpCenter />} />
        <Route path="/safety" element={<Safety />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/accessibility" element={<AccessibilityStatement />} />
        <Route path="/sound-test" element={<SoundTest />} />
        <Route path="/system-status" element={<Navigate to="/ti-admin/status" replace />} />
        <Route path="/agreements" element={<ProtectedRoute><Agreements /></ProtectedRoute>} />

        {/* Admin Routes - Separate Authentication */}
        <Route path="/ti-admin" element={<AdminLogin />} />
        <Route path="/ti-admin/login" element={<AdminLogin />} />
        <Route path="/ti-admin/dashboard" element={<AdminPanel />} />
        <Route path="/ti-admin/ai-assistant" element={<AdminPanel />} />
        <Route path="/ti-admin/taxes" element={<AdminPanel />} />
        <Route path="/ti-admin/users" element={<AdminPanel />} />
        <Route path="/ti-admin/jobs" element={<AdminPanel />} />
        <Route path="/ti-admin/heroes" element={<AdminPanel />} />
        <Route path="/ti-admin/support" element={<AdminPanel />} />
        <Route path="/ti-admin/referrals" element={<AdminPanel />} />
        <Route path="/ti-admin/signup-bonus" element={<AdminPanel />} />
        <Route path="/ti-admin/moderation" element={<AdminPanel />} />
        <Route path="/ti-admin/categories" element={<AdminPanel />} />
        <Route path="/ti-admin/disputes" element={<AdminPanel />} />
        <Route path="/ti-admin/terms" element={<AdminPanel />} />
        <Route path="/ti-admin/security" element={<AdminPanel />} />
        <Route path="/ti-admin/backups" element={<AdminPanel />} />
        <Route path="/ti-admin/operations" element={<AdminPanel />} />
        <Route path="/ti-admin/status" element={<AdminPanel />} />
        <Route path="/ti-admin/email-preview" element={<AdminPanel />} />
        <Route path="/ti-admin/revenue" element={<AdminPanel />} />
        <Route path="/ti-admin/geo-heatmap" element={<AdminPanel />} />
        <Route path="/ti-admin/announcements" element={<AdminPanel />} />
        <Route path="/ti-admin/audit-trail" element={<AdminPanel />} />
        <Route path="/ti-admin/sms" element={<AdminPanel />} />
        <Route path="/ti-admin/transaction-history" element={<AdminPanel />} />
        <Route path="/ti-admin/reconciliation" element={<AdminPanel />} />
        <Route path="/ti-admin/alerts" element={<AdminPanel />} />
        <Route path="/ti-admin/administrators" element={<AdminPanel />} />
        <Route path="/ti-admin/verifications" element={<AdminDashboard />} />

        {/* Legacy redirects */}
        <Route path="/ti-admin/flagged" element={<Navigate to="/ti-admin/moderation" replace />} />
        <Route path="/ti-admin/job-reports" element={<AdminPanel />} />
        <Route path="/ti-admin/safety" element={<Navigate to="/ti-admin/moderation" replace />} />
        <Route path="/ti-admin/notifications" element={<Navigate to="/ti-admin/announcements" replace />} />
        <Route path="/ti-admin/health" element={<Navigate to="/ti-admin/status" replace />} />
        <Route path="/ti-admin/incidents" element={<Navigate to="/ti-admin/status" replace />} />
        <Route path="/ti-admin/activity-log" element={<Navigate to="/ti-admin/audit-trail" replace />} />
        <Route path="/ti-admin/escrow" element={<Navigate to="/ti-admin/operations" replace />} />
        <Route path="/ti-admin/financial" element={<Navigate to="/ti-admin/revenue" replace />} />
        <Route path="/ti-admin/reports" element={<Navigate to="/ti-admin/job-reports" replace />} />

        {/* Error Pages */}
        <Route path="/401" element={<Unauthorized />} />
        <Route path="/403" element={<Forbidden />} />
        <Route path="/500" element={<ServerError />} />
        <Route path="/503" element={<ServiceUnavailable />} />

        {/* 404 Not Found */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
